import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { LayoutDashboard, FileSpreadsheet, Tag, Settings, LogOut, Hourglass, Shield } from "lucide-react";
import Logo from "@/components/Logo";

const baseNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/retainers", label: "Retainers", icon: FileSpreadsheet, testid: "nav-retainers" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const ADMIN_EMAILS = ["paige@stripesandwillows.com"];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { subscription } = useBilling();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const planLabel = subscription?.plan_name ||
    (subscription?.status === "trialing"
      ? `Trial · ${subscription.days_left}d left`
      : subscription?.status === "trial_expired"
      ? "Trial ended"
      : null);

  const planTone =
    subscription?.status === "active"
      ? "bg-[#EFF3EE] text-[#42624B] border-[#C5D5C8]"
      : subscription?.status === "trial_expired"
      ? "bg-[#FAEEED] text-[#7E332D] border-[#E7BFBA]"
      : "bg-[#F0EADA] text-[#7A5E1F] border-[#E4DFD2]";

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());
  const isPaid = subscription?.status === "active";
  let navItems = [...baseNav];
  // Show "Pricing" only for non-paying users (trial, expired, cancelled).
  // Paid users access plan/billing under Settings → Billing card instead.
  if (!isPaid) {
    navItems.splice(2, 0, { to: "/pricing", label: "Pricing", icon: Tag, testid: "nav-pricing" });
  }
  if (isAdmin) {
    navItems = [...navItems, { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" }];
  }

  return (
    <aside
      className="w-64 border-r border-[#E4DFD2] bg-[#FBFAF6] h-screen fixed left-0 top-0 flex flex-col p-4 z-40"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="px-3 py-2 mb-6">
        <Logo size={28} showTagline />
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={item.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#F0EADA] text-[#1F2326]"
                  : "text-[#69747C] hover:bg-[#F3EFE3] hover:text-[#1F2326]"
              }`
            }
          >
            <item.icon className="w-4 h-4" strokeWidth={2} />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto pt-4 border-t border-[#E4DFD2]">
        {planLabel ? (
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            data-testid="sidebar-plan-badge"
            className={`w-full mb-3 inline-flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-opacity hover:opacity-90 ${planTone}`}
          >
            <span className="inline-flex items-center gap-1.5">
              <Hourglass className="w-3 h-3" />
              {planLabel}
            </span>
            <span className="text-[10px] uppercase tracking-wider opacity-80">
              {subscription?.status === "active" ? "Plan" : "Upgrade"}
            </span>
          </button>
        ) : null}
        <div className="flex items-center gap-3 px-3 py-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-8 h-8 rounded-full border border-[#E4DFD2] object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#F0EADA] flex items-center justify-center text-[#69747C] text-sm font-medium">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#1F2326] truncate" data-testid="sidebar-user-name">
              {user?.name || "Member"}
            </div>
            <div className="text-xs text-[#69747C] truncate">{user?.email}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#69747C] hover:bg-[#F3EFE3] hover:text-[#1F2326] transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
